"use client";

import React, { useState } from "react";
import { TrendingUp, Search, ChartBar as BarChart3, Target, MapPin, ArrowRight, CircleCheck as CheckCircle2, Zap, ShieldCheck, Compass, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
const searchMetrics = [{
  id: 1,
  metric: "Avg. Monthly Local Queries",
  value: "48.5K",
  note: "+18% YoY growth in South West B2B search"
}, {
  id: 2,
  metric: "Local Pack Opportunity",
  value: "64%",
  note: "Unclaimed or poorly optimized Google Profiles"
}, {
  id: 3,
  metric: "Mobile Search Share",
  value: "72%",
  note: "Dominant intent across marine, B2B & local trade"
}, {
  id: 4,
  metric: "Average Top 3 CTR",
  value: "34.2%",
  note: "Organic visibility drives high-intent consultation"
}];
const keywordTrends = [{
  id: 1,
  keyword: "seo consultant plymouth",
  volume: "480/mo",
  difficulty: "Medium",
  intent: "Commercial",
  growth: "+35%"
}, {
  id: 2,
  keyword: "local seo services devon",
  volume: "720/mo",
  difficulty: "Low-Med",
  intent: "Commercial",
  growth: "+28%"
}, {
  id: 3,
  keyword: "b2b marketing agency plymouth",
  volume: "390/mo",
  difficulty: "Medium",
  intent: "Transactional",
  growth: "+19%"
}, {
  id: 4,
  keyword: "digital strategy consultant southwest",
  volume: "260/mo",
  difficulty: "Low",
  intent: "Informational",
  growth: "+42%"
}, {
  id: 5,
  keyword: "ecommerce seo specialist devon",
  volume: "320/mo",
  difficulty: "Medium",
  intent: "Commercial",
  growth: "+22%"
}];
const marketOpportunities = [{
  id: 1,
  title: "Marine & Defense B2B Visibility Gap",
  description: "Plymouth's strong maritime and tech sectors often miss out on international search demand due to technical site architectures and weak structured data.",
  badge: "High Impact"
}, {
  id: 2,
  title: "Google Business Profile Under-Optimization",
  description: "Over 60% of local service businesses in Plymouth lack geo-targeted category signals, citation consistency, and localized review strategy.",
  badge: "Quick Win"
}, {
  id: 3,
  title: "AI Search & GEO Readiness",
  description: "As AI overviews expand, Plymouth brands that structure entity relationships early gain prime positioning in generative search results.",
  badge: "Future Proof"
}];
const regionalLinks = [{
  id: 1,
  city: "Norwich SEO Strategy",
  path: "/LocalSeoConsultantNorwichPage",
  label: "Norwich Market Analysis"
}, {
  id: 2,
  city: "Milton Keynes SEO",
  path: "/LocalSeoConsultantMiltonKeynesPage",
  label: "Milton Keynes Hub"
}, {
  id: 3,
  city: "Derby Local SEO",
  path: "/LocalSeoConsultantDerbyPage",
  label: "Derby Search Insights"
}, {
  id: 4,
  city: "National SEO Framework",
  path: "/NationalSeoPage",
  label: "National Coverage"
}, {
  id: 5,
  city: "Technical SEO Audits",
  path: "/TechnicalSeoPage",
  label: "Technical Capabilities"
}, {
  id: 6,
  city: "AI Search Optimisation",
  path: "/AISearchOptimisationPage",
  label: "AI & GEO Search"
}];
export default function Plymouth() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState<"overview" | "keywords" | "competition">("overview");
  const fadeInUp = shouldReduceMotion ? {} : {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.5
    }
  };
  return <section id="plymouthmarketanalysis" data-nav="light" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        
        {}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground border-transparent">
            <MapPin className="w-3.5 h-3.5 mr-1 text-primary" /> Regional Market Intelligence
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default">
            Plymouth Search Trends &amp; Local Market Opportunities
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground font-default leading-relaxed">
            I analyze local search behaviors, competitive landscapes, and high-intent keyword opportunities across Plymouth and Devon to build tailored growth strategies for ambitious businesses.
          </p>
        </div>

        {}
        <div className="flex justify-center mb-10">
          <div className="inline-flex p-1 bg-muted rounded-lg border border-border">
            <Button variant={activeTab === "overview" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("overview")} className="rounded-md font-medium text-xs md:text-sm">
              <BarChart3 className="w-4 h-4 mr-1.5" /> Market Overview
            </Button>
            <Button variant={activeTab === "keywords" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("keywords")} className="rounded-md font-medium text-xs md:text-sm">
              <Search className="w-4 h-4 mr-1.5" /> Key Search Queries
            </Button>
            <Button variant={activeTab === "competition" ? "default" : "ghost"} size="sm" onClick={() => setActiveTab("competition")} className="rounded-md font-medium text-xs md:text-sm">
              <Target className="w-4 h-4 mr-1.5" /> Competitive Gap
            </Button>
          </div>
        </div>

        {}
        {activeTab === "overview" && <motion.div {...fadeInUp} className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {searchMetrics.map((item, index) => <Card key={item.id} data-index={index} className="bg-card border-border shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <CardDescription className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {item.metric}
                    </CardDescription>
                    <CardTitle className="text-3xl font-bold text-primary font-default">
                      {item.value}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground flex items-center">
                      <TrendingUp className="w-3.5 h-3.5 mr-1 text-chart-4 shrink-0" />
                      {item.note}
                    </p>
                  </CardContent>
                </Card>)}
            </div>

            {}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {marketOpportunities.map((opp, index) => <Card key={opp.id} data-index={index} className="bg-card border-border shadow-sm flex flex-col justify-between">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline" className="text-xs border-primary/30 text-primary bg-primary/5">
                        {opp.badge}
                      </Badge>
                      <Sparkles className="w-4 h-4 text-accent" />
                    </div>
                    <CardTitle className="text-lg font-semibold text-foreground font-default">
                      {opp.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {opp.description}
                    </p>
                  </CardContent>
                </Card>)}
            </div>
          </motion.div>}

        {}
        {activeTab === "keywords" && <motion.div {...fadeInUp} className="bg-card rounded-xl border border-border p-6 shadow-sm">
            <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-foreground font-default">High-Intent Plymouth Search Terms</h3>
                <p className="text-sm text-muted-foreground">Sample dataset of high-conversion search queries tracked across Devon regional markets.</p>
              </div>
              <Button asChild variant="outline" size="sm">
                <Link to="/LocalSEOPage">
                  View Full Local SEO Framework <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-muted-foreground font-medium">
                    <th className="p-3 rounded-l-md">Target Keyword</th>
                    <th className="p-3">Est. Monthly Volume</th>
                    <th className="p-3">Search Intent</th>
                    <th className="p-3">Keyword Difficulty</th>
                    <th className="p-3 rounded-r-md">Annual Demand Growth</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {keywordTrends.map((row, index) => <tr key={row.id} data-index={index} className="hover:bg-muted/30 transition-colors">
                      <td className="p-3 font-medium text-foreground flex items-center gap-2">
                        <Search className="w-3.5 h-3.5 text-primary" />
                        {row.keyword}
                      </td>
                      <td className="p-3 text-muted-foreground font-mono">{row.volume}</td>
                      <td className="p-3">
                        <Badge variant="secondary" className="text-xs font-normal">
                          {row.intent}
                        </Badge>
                      </td>
                      <td className="p-3 text-muted-foreground">{row.difficulty}</td>
                      <td className="p-3 font-semibold text-chart-4">{row.growth}</td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </motion.div>}

        {}
        {activeTab === "competition" && <motion.div {...fadeInUp} className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="space-y-6">
              <div className="space-y-2">
                <Badge variant="outline" className="text-primary border-primary">My Plymouth Competitive Insights</Badge>
                <h3 className="text-2xl font-bold text-foreground font-default">Why Most Plymouth Businesses Stagnate On Page 2</h3>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed">
                When I conduct SEO audits for businesses in Plymouth, I consistently find three major strategy gaps: over-reliance on local directory listings, weak technical site speed optimization, and unstructured thin content that fails search engine intent checks.
              </p>
              
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-foreground text-sm">Direct Consultant Access</h4>
                    <p className="text-xs text-muted-foreground">You work directly with me—no account managers or junior delegation.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-foreground text-sm">Hyper-Local Entity Mapping</h4>
                    <p className="text-xs text-muted-foreground">Connecting your brand to localized Devon geographic signals and trust nodes.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-foreground text-sm">Transparent ROI Reporting</h4>
                    <p className="text-xs text-muted-foreground">Clear deliverables, traffic growth metrics, and organic lead acquisition tracking.</p>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <Button asChild variant="default" size="lg" className="rounded-md">
                  <Link to="/ContactPage">
                    Book a Free SEO Consultation <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>

            <Card className="bg-card border-border shadow-md">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-foreground font-default flex items-center gap-2">
                  <Compass className="w-5 h-5 text-primary" /> Plymouth Actionable Roadmap
                </CardTitle>
                <CardDescription>Step-by-step framework I execute to gain local dominance.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="p-3 bg-muted/60 rounded-md border border-border">
                  <span className="font-bold text-primary mr-2">Phase 1:</span>
                  <span className="text-foreground font-medium">Technical &amp; Local Signals Audit</span>
                  <p className="text-xs text-muted-foreground mt-1">Eliminating indexation errors, fixing local citations, and mapping Plymouth intent.</p>
                </div>
                <div className="p-3 bg-muted/60 rounded-md border border-border">
                  <span className="font-bold text-primary mr-2">Phase 2:</span>
                  <span className="text-foreground font-medium">Content Architecture &amp; Siloing</span>
                  <p className="text-xs text-muted-foreground mt-1">Creating high-authority service pages tailored to South West decision-makers.</p>
                </div>
                <div className="p-3 bg-muted/60 rounded-md border border-border">
                  <span className="font-bold text-primary mr-2">Phase 3:</span>
                  <span className="text-foreground font-medium">Local Trust &amp; Entity Building</span>
                  <p className="text-xs text-muted-foreground mt-1">Securing genuine regional editorial mentions and Google Knowledge Graph connections.</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>}

        <Separator className="my-12 md:my-16" />

        {}
        <div className="space-y-6">
          <div className="text-center max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-foreground font-default">Cross-Regional Local SEO Coverage</h3>
            <p className="text-xs md:text-sm text-muted-foreground mt-1">
              Explore localized SEO insights and dedicated market analysis across neighboring UK commercial hubs.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {regionalLinks.map((link, index) => <Button key={link.id} data-index={index} asChild variant="outline" className="h-auto py-3 px-3 text-xs flex flex-col items-center justify-center text-center hover:border-primary hover:bg-primary/5 transition-all">
                <Link to={link.path}>
                  <MapPin className="w-3.5 h-3.5 text-primary mb-1 shrink-0" />
                  <span className="font-medium text-foreground line-clamp-1">{link.city}</span>
                </Link>
              </Button>)}
          </div>
        </div>

        {}
        <div className="mt-12 bg-primary text-primary-foreground rounded-2xl p-8 md:p-10 shadow-lg text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 max-w-2xl">
            <h3 className="text-2xl md:text-3xl font-bold font-default text-primary-foreground">
              Ready to grow your Plymouth business search traffic?
            </h3>
            <p className="text-sm md:text-base text-primary-foreground/90 font-default">
              You will work directly with me, Yassine Essaadi, to audit your site, uncover competitor weaknesses, and execute a high-converting SEO strategy.
            </p>
          </div>
          <div className="shrink-0 flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <Button asChild variant="secondary" size="lg" className="font-semibold text-secondary-foreground">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
              <Link to="/CaseStudiesPage">
                Explore Case Studies
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}